/**
 * APSelect Component
 *
 * A comprehensive select dropdown component following Figma design system specifications.
 * Supports multiple sizes, states, accessibility features, clearable functionality, and custom options.
 * Based on native HTML select behavior with enhanced UX.
 *
 * @example
 * ```tsx
 * <APSelect
 *   size="medium"
 *   placeholder="Choose an option"
 *   options={[
 *     { value: '5', label: '5' },
 *     { value: '10', label: '10' },
 *     { value: '25', label: '25' }
 *   ]}
 *   value={selectedValue}
 *   onChange={handleChange}
 *   clearable
 * />
 *
 * <APSelect
 *   size="large"
 *   label="Items per page"
 *   description="Select how many items to show per page"
 *   options={options}
 *   value={value}
 *   onChange={onChange}
 *   clearable
 *   onClear={handleClear}
 *   required
 * />
 * ```
 */

import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import type { IAPSelectProps } from './types';

import './styles.scss';

import { APSpinner, APTypography } from '@shared/components';

import APSelectMenu from './components/menu';
import APSelectTrigger from './components/trigger';

/**
 * APSelect - Production-ready select dropdown component
 *
 * Follows AstroPay design system and accessibility guidelines.
 * Based on Figma Select component specifications.
 */
export const APSelect = forwardRef<HTMLDivElement, IAPSelectProps>(
  (
    {
      size = 'medium',
      variant = 'default',
      value,
      placeholder = 'Select an option',
      options = [],
      label,
      description,
      disabled = false,
      required = false,
      fullWidth = false,
      clearable = false,
      error,
      className = '',
      'data-testid': testId,
      onChange,
      onClear,
      onFocus,
      onBlur,
      icon,
      maxMenuHeight = 200,
      searchable = false,
      searchPlaceholder = 'Search...',
      loading = false,
      ...rest
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState<{
      top?: number;
      bottom?: number;
      left: number;
      width: number;
      openUpward: boolean;
    } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Find selected option
    const selectedOption = options.find((option) => option.value === value);

    // Calculate menu position for fixed positioning (viewport-relative)
    const calculateMenuPosition = useCallback(() => {
      if (!containerRef.current) return null;

      const rect = containerRef.current.getBoundingClientRect();
      const menuHeight = 240; // approximate max menu height
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Open upward if not enough space below and more space above
      if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        return {
          bottom: window.innerHeight - rect.top,
          left: rect.left,
          width: rect.width,
          openUpward: true,
        };
      }

      return {
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
        openUpward: false,
      };
    }, []);

    /*
     * Opening and closing go through this single pair, because the menu only renders when
     * `menuPosition` is set: any path that flips `isOpen` without also measuring produces an open
     * combobox with no menu. The keyboard handlers used to do exactly that, which made every
     * APSelect unopenable by keyboard.
     */
    const openMenu = useCallback(() => {
      // Position is always recalculated — the menu is portalled to document.body.
      setMenuPosition(calculateMenuPosition());
      setIsOpen(true);
      onFocus?.();
    }, [calculateMenuPosition, onFocus]);

    const closeMenu = useCallback(() => {
      setMenuPosition(null);
      setIsOpen(false);
      onBlur?.();
    }, [onBlur]);

    // Handle trigger click
    const handleTriggerClick = useCallback(() => {
      if (!disabled && !loading) {
        if (isOpen) {
          closeMenu();
        } else {
          openMenu();
        }
      }
    }, [disabled, loading, isOpen, openMenu, closeMenu]);

    // Handle clear action
    const handleClear = useCallback(() => {
      onChange?.('' as string | number); // Clear the value
      onClear?.();
      setIsOpen(false);
      onBlur?.();
    }, [onChange, onClear, onBlur]);

    // Handle option selection
    const handleOptionSelect = useCallback(
      (optionValue: string | number) => {
        onChange?.(optionValue);
        setIsOpen(false);
        onBlur?.();
      },
      [onChange, onBlur]
    );

    // Handle menu close
    const handleMenuClose = useCallback(() => {
      setIsOpen(false);
      setMenuPosition(null);
      onBlur?.();
    }, [onBlur]);

    // Update position when menu is open and window is resized/scrolled
    useEffect(() => {
      if (!isOpen || !menuPosition) return;

      const updatePosition = () => {
        const position = calculateMenuPosition();
        setMenuPosition(position);
      };

      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }, [isOpen, menuPosition, calculateMenuPosition]);

    // Handle keyboard navigation
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (disabled || loading) return;

        switch (event.key) {
          case 'Enter':
          case ' ': {
            event.preventDefault();
            if (isOpen) {
              closeMenu();
            } else {
              openMenu();
            }
            break;
          }
          case 'ArrowDown':
          case 'ArrowUp':
            event.preventDefault();
            if (!isOpen) {
              openMenu();
            }
            break;
          case 'Escape':
            if (isOpen) {
              closeMenu();
            }
            break;
        }
      },
      [disabled, loading, isOpen, openMenu, closeMenu]
    );

    // Construct container class names
    const containerClasses = [
      'apds-select',
      `apds-select--${size}`,
      `apds-select--${variant}`,
      fullWidth && 'apds-select--full-width',
      disabled && 'apds-select--disabled',
      error && 'apds-select--error',
      loading && 'apds-select--loading',
      clearable && 'apds-select--clearable',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={containerClasses} data-testid={testId} {...rest}>
        {/* Label */}
        {label && (
          <APTypography
            as="label"
            size="small"
            weight="500"
            color="base"
            className={`apds-select__label ${required ? 'apds-select__label--required' : ''}`}
          >
            {label}
          </APTypography>
        )}

        {/* Select trigger */}
        <div className="apds-select__trigger-container" ref={containerRef}>
          {loading ? (
            <div className="apds-select__trigger apds-select__trigger--loading">
              <div className="apds-select__loading">
                <APSpinner size="small" variant="primary" />
              </div>
            </div>
          ) : (
            <APSelectTrigger
              size={size}
              variant={variant}
              isOpen={isOpen}
              disabled={disabled}
              clearable={clearable}
              placeholder={placeholder}
              selectedLabel={selectedOption?.label}
              icon={icon}
              onClick={handleTriggerClick}
              onClear={handleClear}
              onKeyDown={handleKeyDown}
            />
          )}
        </div>

        {/* Select menu — rendered via portal at document.body to escape overflow clipping */}
        {isOpen &&
          !loading &&
          menuPosition &&
          createPortal(
            <div
              ref={menuRef}
              className="apds-select__menu-container apds-select__menu-container--portal"
              style={{
                position: 'fixed',
                ...(menuPosition.openUpward
                  ? { bottom: `${menuPosition.bottom}px`, top: 'auto' }
                  : { top: `${menuPosition.top}px` }),
                left: `${menuPosition.left}px`,
                width: `${menuPosition.width}px`,
                zIndex: 999999,
              }}
            >
              <APSelectMenu
                options={options}
                selectedValue={value}
                size={size}
                maxHeight={maxMenuHeight}
                searchable={searchable}
                searchPlaceholder={searchPlaceholder}
                onSelect={handleOptionSelect}
                onClose={handleMenuClose}
              />
            </div>,
            document.body
          )}

        {/* Description or Error */}
        {error ? (
          <APTypography as="p" size="small" weight="400" color="error" className="apds-select__error">
            {error}
          </APTypography>
        ) : description ? (
          <APTypography
            as="p"
            size="small"
            weight="400"
            color="muted"
            className="apds-select__description"
          >
            {description}
          </APTypography>
        ) : null}
      </div>
    );
  }
);

APSelect.displayName = 'APSelect';

export type { IAPSelectProps };
export default APSelect;
